import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Inbox, LogIn, ArrowLeft, Mail, Loader2, CalendarCheck, CreditCard, PenLine, Users } from "lucide-react";
import { usePageMeta } from "@/hooks/usePageMeta";
import { EnsoMark } from "@/components/Logo";
import { WishlistsSection } from "@/components/WishlistsSection";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const googleSignIn = () => {
  const redirectUrl = window.location.origin + "/enquiries";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

const formatApiErrorDetail = (detail) => {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
};

const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const money = (amountMinor, currency) =>
  new Intl.NumberFormat("en-GB", { style: "currency", currency: (currency || "gbp").toUpperCase() })
    .format((amountMinor || 0) / 100);

const statusTone = (status) =>
  status === "paid"
    ? "bg-jade/10 text-jade"
    : status === "failed" || status === "expired" || status === "refunded"
    ? "bg-red-700/10 text-red-700"
    : "bg-ink2/10 text-ink2";

const EmailSignIn = ({ onSignedIn }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError("");
    try {
      const res = await fetch(`${API}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(formatApiErrorDetail(data.detail));
      onSignedIn(data);
    } catch (err) {
      setError(err.message || "Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="mt-6 space-y-4" data-testid="email-signin-form">
      <div>
        <label htmlFor="signin-email" className="mb-2 block text-xs font-medium uppercase tracking-[0.15em] text-ink2">
          Email
        </label>
        <input
          id="signin-email"
          type="email"
          data-testid="signin-email-input"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@yourdomain.com"
          required
          className="w-full rounded-xl border border-jade/20 bg-paper/60 px-4 py-3 text-sm text-ink placeholder:text-ink2/40 transition-colors duration-300"
        />
      </div>
      <div>
        <label htmlFor="signin-password" className="mb-2 block text-xs font-medium uppercase tracking-[0.15em] text-ink2">
          Password
        </label>
        <input
          id="signin-password"
          type="password"
          data-testid="signin-password-input"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="Your password"
          required
          className="w-full rounded-xl border border-jade/20 bg-paper/60 px-4 py-3 text-sm text-ink placeholder:text-ink2/40 transition-colors duration-300"
        />
      </div>
      {error && (
        <p className="text-sm text-red-700" data-testid="signin-error">
          {error}
        </p>
      )}
      <button
        type="submit"
        disabled={busy}
        data-testid="email-signin-button"
        className="inline-flex w-full items-center justify-center gap-3 rounded-full bg-jade px-7 py-3.5 text-sm font-medium text-paper transition-colors duration-300 hover:bg-ink disabled:cursor-not-allowed disabled:opacity-60"
      >
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
        {busy ? "Signing in…" : "Sign in with email"}
      </button>
    </form>
  );
};

const EMPTY_POST = {
  title: "",
  summary: "",
  tag: "",
  cover_image: "",
  body: "",
  published: false,
};

const PostForm = ({ initial, onSaved, onCancel }) => {
  const [form, setForm] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const update = (key) => (e) =>
    setForm((f) => ({ ...f, [key]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const save = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError("");
    try {
      const isEdit = Boolean(initial.slug);
      const res = await fetch(`${API}/posts${isEdit ? `/${initial.slug}` : ""}`, {
        method: isEdit ? "PUT" : "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(form),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(formatApiErrorDetail(data.detail));
      onSaved();
    } catch (err) {
      setError(err.message || "Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const inputClass =
    "w-full rounded-xl border border-jade/20 bg-paper/60 px-4 py-3 text-sm text-ink placeholder:text-ink2/40 transition-colors duration-300";

  return (
    <form onSubmit={save} className="mt-6 space-y-4 rounded-2xl bg-white p-8 shadow-ambient" data-testid="post-form">
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="sm:col-span-2">
          <label className="mb-2 block text-xs font-medium uppercase tracking-[0.15em] text-ink2">Title</label>
          <input data-testid="post-title-input" className={inputClass} value={form.title} onChange={update("title")} required maxLength={120} placeholder="An honest headline" />
        </div>
        <div className="sm:col-span-2">
          <label className="mb-2 block text-xs font-medium uppercase tracking-[0.15em] text-ink2">One-line summary</label>
          <input data-testid="post-summary-input" className={inputClass} value={form.summary} onChange={update("summary")} required maxLength={220} placeholder="What a reader gets from this" />
        </div>
        <div>
          <label className="mb-2 block text-xs font-medium uppercase tracking-[0.15em] text-ink2">Topic tag</label>
          <input data-testid="post-tag-input" className={inputClass} value={form.tag} onChange={update("tag")} required maxLength={40} placeholder="Websites" />
        </div>
        <div>
          <label className="mb-2 block text-xs font-medium uppercase tracking-[0.15em] text-ink2">Cover image URL (https)</label>
          <input data-testid="post-cover-input" className={inputClass} value={form.cover_image} onChange={update("cover_image")} required maxLength={500} placeholder="https://…" />
        </div>
        <div className="sm:col-span-2">
          <label className="mb-2 block text-xs font-medium uppercase tracking-[0.15em] text-ink2">
            Body (lines starting with “# ” become headings, “- ” become bullets)
          </label>
          <textarea data-testid="post-body-input" className={`${inputClass} min-h-[220px] resize-y font-mono text-xs`} value={form.body} onChange={update("body")} required placeholder={"# A section\nA short paragraph.\n- A bullet"} />
        </div>
      </div>
      <label className="flex items-center gap-3 text-sm text-ink2" data-testid="post-published-toggle">
        <input type="checkbox" checked={form.published} onChange={update("published")} className="h-4 w-4 accent-[#235347]" />
        Published (visible on the site)
      </label>
      {error && <p className="text-sm text-red-700" data-testid="post-form-error">{error}</p>}
      <div className="flex gap-3">
        <button type="submit" disabled={busy} data-testid="post-save-button" className="rounded-full bg-jade px-7 py-3 text-sm font-medium text-paper transition-colors duration-300 hover:bg-ink disabled:cursor-not-allowed disabled:opacity-60">
          {busy ? "Saving…" : initial.slug ? "Save changes" : "Create post"}
        </button>
        <button type="button" onClick={onCancel} data-testid="post-cancel-button" className="rounded-full border border-jade/30 px-7 py-3 text-sm font-medium text-jade transition-colors duration-300 hover:bg-jade/5">
          Cancel
        </button>
      </div>
    </form>
  );
};

const Enquiries = () => {
  usePageMeta("Vagus Digital | Owner dashboard");
  const location = useLocation();
  const [user, setUser] = useState(location.state?.user || null);
  const [checking, setChecking] = useState(!location.state?.user);
  const [enquiries, setEnquiries] = useState(null);
  const [bookings, setBookings] = useState(null);
  const [payments, setPayments] = useState(null);
  const [posts, setPosts] = useState(null);
  const [subscribers, setSubscribers] = useState(null);
  const [denied, setDenied] = useState(false);
  const [authError, setAuthError] = useState(Boolean(location.state?.authError));
  const [showEmailSignIn, setShowEmailSignIn] = useState(false);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (!user) return;
    const get = (path, setter) =>
      fetch(`${API}${path}`, { credentials: "include" })
        .then(async (res) => {
          if (res.status === 403) {
            setDenied(true);
            return;
          }
          if (!res.ok) throw new Error("failed");
          return res.json();
        })
        .then((data) => data && setter(data))
        .catch(() => setter(null));
    get("/enquiries", (d) => setEnquiries(d.enquiries || []));
    get("/bookings", (d) => setBookings(d.bookings || []));
    get("/payments", (d) => setPayments(d.payments || []));
    get("/posts/manage", (d) => setPosts(d.posts || []));
    get("/newsletter", (d) => setSubscribers(d.subscribers || []));
  }, [user]);

  useEffect(() => {
    if (user || authError) return;
    fetch(`${API}/auth/me`, { credentials: "include" })
      .then(async (res) => {
        if (!res.ok) throw new Error("unauthenticated");
        return res.json();
      })
      .then((data) => {
        setUser(data);
        setChecking(false);
      })
      .catch(() => {
        setChecking(false);
      });
  }, [user, authError]);

  const signOut = async () => {
    await fetch(`${API}/auth/logout`, { method: "POST", credentials: "include" }).catch(() => {});
    setUser(null);
    setEnquiries(null);
    setBookings(null);
    setPayments(null);
    setPosts(null);
    setSubscribers(null);
    setDenied(false);
    setShowEmailSignIn(false);
    setShowForm(false);
    setEditing(null);
  };

  const togglePublished = async (post) => {
    const res = await fetch(`${API}/posts/${post.slug}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ ...post, published: !post.published }),
    }).catch(() => null);
    if (res && res.ok) {
      const data = await res.json().catch(() => ({}));
      setPosts((list) => (list || []).map((p) => (p.slug === post.slug ? { ...p, ...data } : p)));
    }
  };

  const deletePost = async (post) => {
    const res = await fetch(`${API}/posts/${post.slug}`, { method: "DELETE", credentials: "include" }).catch(() => null);
    if (res && res.ok) setPosts((list) => (list || []).filter((p) => p.slug !== post.slug));
  };

  const refreshPosts = async () => {
    const res = await fetch(`${API}/posts/manage`, { credentials: "include" }).catch(() => null);
    if (res && res.ok) {
      const data = await res.json();
      setPosts(data.posts || []);
    }
    setShowForm(false);
    setEditing(null);
  };

  return (
    <div className="min-h-screen bg-paper px-6 py-16 lg:px-10">
      <div className="mx-auto max-w-4xl">
        <div className="flex items-center justify-between">
          <Link to="/" data-testid="enquiries-home-link" className="inline-flex items-center gap-2 text-sm font-medium text-jade transition-opacity duration-300 hover:opacity-70">
            <ArrowLeft className="h-4 w-4" /> Back to site
          </Link>
          {user && (
            <button onClick={signOut} data-testid="enquiries-signout-button" className="rounded-full border border-jade/30 px-5 py-2 text-sm font-medium text-jade transition-colors duration-300 hover:bg-jade/5">
              Sign out
            </button>
          )}
        </div>

        <h1 className="mt-10 font-display text-3xl font-light tracking-tight text-ink sm:text-4xl">
          <em className="italic text-jade">Owner</em> dashboard
        </h1>

        {checking && (
          <div className="mt-12 flex items-center gap-3 text-ink2" data-testid="enquiries-checking">
            <EnsoMark className="h-5 w-5 animate-spin text-jade" />
            Checking your session…
          </div>
        )}

        {!checking && !user && (
          <div className="mt-12 rounded-2xl bg-white p-10 shadow-ambient" data-testid="enquiries-signin-card">
            <Inbox className="h-8 w-8 text-jade" strokeWidth={1.75} />
            <p className="mt-5 max-w-md leading-relaxed text-ink2">
              This dashboard shows every enquiry, booking, payment and article
              saved from your site. Sign in with Google, or with your owner
              email and password.
            </p>
            {authError && (
              <p className="mt-4 text-sm text-red-700" data-testid="enquiries-auth-error">
                That sign-in didn't complete. Please try again.
              </p>
            )}
            <div className="mt-7 flex flex-wrap gap-3">
              <button onClick={googleSignIn} data-testid="google-signin-button" className="inline-flex items-center gap-3 rounded-full bg-jade px-7 py-3.5 text-sm font-medium text-paper transition-colors duration-300 hover:bg-ink">
                <LogIn className="h-4 w-4" />
                Sign in with Google
              </button>
              <button onClick={() => setShowEmailSignIn((v) => !v)} data-testid="toggle-email-signin-button" aria-expanded={showEmailSignIn} className="inline-flex items-center gap-3 rounded-full border border-jade/30 px-7 py-3.5 text-sm font-medium text-jade transition-colors duration-300 hover:bg-jade/5">
                <Mail className="h-4 w-4" />
                Email &amp; password
              </button>
            </div>
            {showEmailSignIn && <EmailSignIn onSignedIn={setUser} />}
          </div>
        )}

        {!checking && user && denied && (
          <div className="mt-12 rounded-2xl bg-white p-10 shadow-ambient" data-testid="enquiries-denied">
            <p className="leading-relaxed text-ink2">
              You're signed in as <strong>{user.email}</strong>, but this dashboard
              is only available to the owner account (groupvagus@gmail.com).
            </p>
          </div>
        )}

        {!checking && user && !denied && (
          <div className="mt-10 space-y-12">
            <section data-testid="dashboard-posts">
              <div className="flex items-center justify-between">
                <h2 className="flex items-center gap-3 font-display text-2xl font-medium text-ink">
                  <PenLine className="h-5 w-5 text-jade" /> Insights posts
                </h2>
                <button
                  onClick={() => {
                    setEditing(null);
                    setShowForm(true);
                  }}
                  data-testid="new-post-button"
                  className="rounded-full bg-jade px-5 py-2 text-sm font-medium text-paper transition-colors duration-300 hover:bg-ink"
                >
                  Write a post
                </button>
              </div>
              {showForm && (
                <PostForm
                  initial={editing || EMPTY_POST}
                  onSaved={refreshPosts}
                  onCancel={() => {
                    setShowForm(false);
                    setEditing(null);
                  }}
                />
              )}
              {posts === null && !showForm && <p className="mt-4 text-sm text-ink2">Loading posts…</p>}
              {Array.isArray(posts) && (
                <ul className="mt-5 space-y-3" data-testid="posts-list">
                  {posts.map((post) => (
                    <li key={post.slug} className="flex flex-wrap items-center justify-between gap-3 rounded-2xl bg-white p-6 shadow-ambient" data-testid={`manage-post-${post.slug}`}>
                      <div>
                        <p className="font-display text-lg font-medium text-ink">{post.title}</p>
                        <p className="mt-0.5 text-xs text-ink2/60">
                          {post.tag} · {post.reading_time} min · {post.published ? `Published ${formatDate(post.published_at)}` : "Draft"}
                        </p>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <button
                          onClick={() => togglePublished(post)}
                          data-testid={`publish-toggle-${post.slug}`}
                          className={`rounded-full px-4 py-2 text-xs font-medium transition-colors duration-300 ${
                            post.published ? "bg-jade/10 text-jade hover:bg-jade/20" : "bg-jade text-paper hover:bg-ink"
                          }`}
                        >
                          {post.published ? "Unpublish" : "Publish"}
                        </button>
                        <button
                          onClick={() => {
                            setEditing(post);
                            setShowForm(true);
                          }}
                          data-testid={`edit-post-${post.slug}`}
                          className="rounded-full border border-jade/25 px-4 py-2 text-xs font-medium text-jade transition-colors duration-300 hover:bg-jade/5"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => deletePost(post)}
                          data-testid={`delete-post-${post.slug}`}
                          className="rounded-full border border-red-700/25 px-4 py-2 text-xs font-medium text-red-700 transition-colors duration-300 hover:bg-red-700/5"
                        >
                          Delete
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            <section data-testid="dashboard-enquiries">
              <h2 className="flex items-center gap-3 font-display text-2xl font-medium text-ink">
                <Inbox className="h-5 w-5 text-jade" /> Enquiries
              </h2>
              {enquiries === null && <p className="mt-4 text-sm text-ink2">Loading enquiries…</p>}
              {Array.isArray(enquiries) && enquiries.length === 0 && (
                <p className="mt-4 rounded-2xl bg-white p-6 text-sm leading-relaxed text-ink2 shadow-ambient">
                  No enquiries yet. Contact-form messages appear here even when email delivery is paused.
                </p>
              )}
              {Array.isArray(enquiries) && enquiries.length > 0 && (
                <ul className="mt-5 space-y-4" data-testid="enquiries-list">
                  {enquiries.map((enquiry, i) => (
                    <li key={i} className="rounded-2xl bg-white p-7 shadow-ambient" data-testid="enquiry-item">
                      <div className="flex flex-wrap items-baseline justify-between gap-2">
                        <p className="font-display text-xl font-medium text-ink">{enquiry.name}</p>
                        <p className="text-xs text-ink2/60">{formatDate(enquiry.created_at)}</p>
                      </div>
                      <a href={`mailto:${enquiry.email}`} data-testid={`enquiry-email-${i}`} className="mt-1 inline-block text-sm font-medium text-jade underline-offset-4 hover:underline">
                        {enquiry.email}
                      </a>
                      <p className="mt-4 whitespace-pre-wrap text-sm leading-relaxed text-ink2">{enquiry.message}</p>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            <section data-testid="dashboard-bookings">
              <h2 className="flex items-center gap-3 font-display text-2xl font-medium text-ink">
                <CalendarCheck className="h-5 w-5 text-jade" /> Bookings
              </h2>
              {bookings === null && <p className="mt-4 text-sm text-ink2">Loading bookings…</p>}
              {Array.isArray(bookings) && bookings.length === 0 && (
                <p className="mt-4 rounded-2xl bg-white p-6 text-sm leading-relaxed text-ink2 shadow-ambient">
                  No booking requests yet. They'll appear here from the /book page.
                </p>
              )}
              {Array.isArray(bookings) && bookings.length > 0 && (
                <ul className="mt-5 space-y-4" data-testid="bookings-list">
                  {bookings.map((booking, i) => (
                    <li key={i} className="rounded-2xl bg-white p-7 shadow-ambient" data-testid="booking-item">
                      <div className="flex flex-wrap items-baseline justify-between gap-2">
                        <p className="font-display text-xl font-medium text-ink">{booking.name}</p>
                        <span className="rounded-full bg-jade/10 px-3 py-1 text-xs font-medium text-jade">{booking.service}</span>
                      </div>
                      <p className="mt-1 text-sm font-medium text-jade" data-testid={`booking-slot-${i}`}>
                        {booking.date} · {booking.slot} UK time
                      </p>
                      <a href={`mailto:${booking.email}`} data-testid={`booking-email-${i}`} className="mt-1 inline-block text-sm text-ink2 underline-offset-4 hover:underline">
                        {booking.email}
                      </a>
                      {booking.notes && <p className="mt-3 whitespace-pre-wrap text-sm leading-relaxed text-ink2">{booking.notes}</p>}
                    </li>
                  ))}
                </ul>
              )}
            </section>

            <section data-testid="dashboard-payments">
              <h2 className="flex items-center gap-3 font-display text-2xl font-medium text-ink">
                <CreditCard className="h-5 w-5 text-jade" /> Payments
              </h2>
              {payments === null && <p className="mt-4 text-sm text-ink2">Loading payments…</p>}
              {Array.isArray(payments) && payments.length === 0 && (
                <p className="mt-4 rounded-2xl bg-white p-6 text-sm leading-relaxed text-ink2 shadow-ambient">
                  No payments yet. Deposits paid through /pay appear here.
                </p>
              )}
              {Array.isArray(payments) && payments.length > 0 && (
                <ul className="mt-5 space-y-4" data-testid="payments-list">
                  {payments.map((payment, i) => (
                    <li key={i} className="flex flex-wrap items-center justify-between gap-3 rounded-2xl bg-white p-6 shadow-ambient" data-testid="payment-item">
                      <div>
                        <p className="font-display text-lg font-medium text-ink">{money(payment.amount, payment.currency)}</p>
                        <p className="text-xs text-ink2/60">
                          {payment.reference ? `Ref ${payment.reference} · ` : ""}
                          {formatDate(payment.created_at)}
                        </p>
                      </div>
                      <span className={`rounded-full px-3 py-1 text-xs font-medium capitalize ${statusTone(payment.payment_status)}`} data-testid={`payment-status-${i}`}>
                        {payment.payment_status}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            <section data-testid="dashboard-subscribers">
              <h2 className="flex items-center gap-3 font-display text-2xl font-medium text-ink">
                <Users className="h-5 w-5 text-jade" /> Newsletter subscribers
              </h2>
              {subscribers === null && <p className="mt-4 text-sm text-ink2">Loading subscribers…</p>}
              {Array.isArray(subscribers) && subscribers.length === 0 && (
                <p className="mt-4 rounded-2xl bg-white p-6 text-sm leading-relaxed text-ink2 shadow-ambient">
                  No subscribers yet. Signups from the footer appear here.
                </p>
              )}
              {Array.isArray(subscribers) && subscribers.length > 0 && (
                <ul className="mt-5 space-y-2" data-testid="subscribers-list">
                  {subscribers.map((sub, i) => (
                    <li key={i} className="flex items-center justify-between rounded-xl bg-white px-6 py-4 shadow-ambient" data-testid="subscriber-item">
                      <span className="text-sm text-ink">{sub.email}</span>
                      <span className="text-xs text-ink2/50">{formatDate(sub.created_at)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            <WishlistsSection />
          </div>
        )}
      </div>
    </div>
  );
};

export default Enquiries;